/**
 * The feature catalogue — what a plan can include.
 *
 * Three flat boolean keys. They live in `plans.features` (an allow list: a
 * missing key means not granted) merged under
 * `billing_accounts.features_override`, and reach the client through
 * `get_entitlements`. The catalogue is code, not a table, because a key
 * without a gate in code does nothing — adding a feature is a code change
 * anyway.
 *
 *   image   the image studio as ONE feature: Models, Scenes, Try-on and
 *           Sketch to product. They share an entity graph (models feed scenes,
 *           both feed looks, sketches feed the catalogue), so gating them one
 *           by one would leave pickers, cards and Iris pointing at pages the
 *           user cannot reach.
 *   video   Lumoo Video — its own entity and page, nothing else links into it.
 *   iris    the agent, an overlay on whatever else is on.
 *
 * Upscale, creation (Iris's in-chat render) and studio (a Lumoo Studio canvas
 * render) are capabilities inside these, not features.
 *
 * Two kinds of plan use the same keys differently. A SELF-SERVE plan grants a
 * set and the account override holds exceptions. A MANAGED plan (Enterprise)
 * grants nothing: it is the shape of a contract negotiated outside the
 * platform, and the account override IS the contract, every key explicit.
 *
 * Features are enforced in the FRONTEND only — navigation and route redirects.
 * They are packaging, not money: a user who bypasses the UI to reach a vertical
 * outside their tier still pays list-price credits for it. Credits are the
 * backend enforcement (`check_workspace_credits` before every charged call).
 */
import { Clapperboard, Image, type LucideIcon, Sparkles } from 'lucide-react'
import { FEATURE_KEYS, type FeatureKey, type Features } from './feature-keys'

export { FEATURE_KEYS, type FeatureKey, type Features }

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  image: 'Images',
  video: 'Video',
  iris: 'Iris',
}

/** The same icons the workspace navigation uses for each area. */
export const FEATURE_ICONS: Record<FeatureKey, LucideIcon> = {
  image: Image,
  video: Clapperboard,
  iris: Sparkles,
}

export function hasFeature(features: Features, key: FeatureKey): boolean {
  return features[key] === true
}

/**
 * Reads the jsonb from `get_entitlements` into the typed shape, keeping only
 * known keys with boolean values. Unknown keys (a retired feature, a typo in
 * an override) are dropped rather than trusted.
 */
export function parseFeatures(raw: unknown): Features {
  if (!raw || typeof raw !== 'object') {
    return {}
  }
  const features: Features = {}
  for (const key of FEATURE_KEYS) {
    const value = (raw as Record<string, unknown>)[key]
    if (typeof value === 'boolean') {
      features[key] = value
    }
  }
  return features
}
